import { isSoldOut } from '@/lib/stock';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, ArrowRight } from 'lucide-react';
import { shopifyImg } from '@/lib/shopify';
import { trackGenericEvent } from '@/lib/crm/track';
import { cn } from '@/lib/utils';
import maosImg from '@/assets/home-v2/maos-mesa.webp.asset.json';
import pesImg from '@/assets/home-v2/pes-pack.webp.asset.json';
import corpoImg from '@/assets/home-v2/corpo-aloe.webp.asset.json';
import sabImg from '@/assets/home-v2/sab-pack.webp.asset.json';
import balImg from '@/assets/home-v2/bal-pack.webp.asset.json';
import { useProductsByHandle, useAddToCart, useViewItemList, PriceBlock, Reveal, Eyebrow, H2 } from './shared';
import { PurchaseBenefits } from './PurchaseBenefits';

type Tone = 'maos' | 'pes' | 'corpo' | 'sab' | 'bal';

const TONE: Record<Tone, { dot: string; bg: string; btn: string; text: string }> = {
  maos: { dot: 'bg-v2-maos', bg: 'bg-v2-maos/10', btn: 'bg-v2-maos hover:bg-v2-maos/90', text: 'text-v2-maos' },
  pes: { dot: 'bg-v2-pes', bg: 'bg-v2-pes/10', btn: 'bg-v2-pes hover:bg-v2-pes/90', text: 'text-v2-pes' },
  corpo: { dot: 'bg-v2-corpo', bg: 'bg-v2-corpo/10', btn: 'bg-v2-corpo hover:bg-v2-corpo/90', text: 'text-v2-corpo' },
  sab: { dot: 'bg-v2-sab', bg: 'bg-v2-sab/10', btn: 'bg-v2-sab hover:bg-v2-sab/90', text: 'text-v2-sab' },
  bal: { dot: 'bg-v2-bal', bg: 'bg-v2-bal/10', btn: 'bg-v2-bal hover:bg-v2-bal/90', text: 'text-v2-bal' },
};

const OPTIONS: Array<{ key: Tone; label: string; handle: string; phrase: string; text: string; actives: string[]; photo: string }> = [
  {
    key: 'maos', label: 'Mãos ressecadas', handle: 'hidratante-para-as-maos-dermoconstrutor-80g',
    phrase: 'Creme para mãos ressecadas de tanto lavar',
    text: 'Para usar quantas vezes precisar ao longo do dia, depois de lavar as mãos. Toque seco, não deixa a mão escorregando.',
    actives: ['Manteiga de karité', 'Extrato de própolis', 'Aloe vera', 'Óleo de rosa-mosqueta'], photo: maosImg.url,
  },
  {
    key: 'pes', label: 'Pés rachados', handle: 'hidratante-para-os-pes-dermoconstrutor-80g',
    phrase: 'Creme para pés rachados e calcanhar seco',
    text: 'Depois do banho, nos pés limpos e secos. Toque seco, dá para calçar logo em seguida.',
    actives: ['Manteiga de karité', 'Alantoína', 'Óleo de rosa-mosqueta'], photo: pesImg.url,
  },
  {
    key: 'corpo', label: 'Corpo seco', handle: 'hidratante-corporal-dermoconstrutor-80g',
    phrase: 'Hidratante corporal sem perfume para pele seca',
    text: 'Toque seco, absorve rápido e não pesa no calor. Para passar depois do banho, todo dia.',
    actives: ['Óleo de rosa-mosqueta', 'Óleo de girassol', 'Aloe vera', 'Alantoína'], photo: corpoImg.url,
  },
  {
    key: 'sab', label: 'Banho', handle: 'sabonete-liquido-extra-suave-150ml',
    phrase: 'Sabonete para pele sensível, corpo e rosto',
    text: 'Limpeza suave, sem perfume, para o banho de todo dia.',
    actives: ['Extrato de camomila', 'Chá verde', 'Ácido hialurônico'], photo: sabImg.url,
  },
  {
    key: 'bal', label: 'Áreas muito secas', handle: 'balsamo-hidratante-corpo-e-rosto-30ml',
    phrase: 'Para áreas muito ressecadas, corpo e rosto',
    text: 'Algumas gotas nas áreas que mais ressecam. Leva óleos essenciais de lavanda e palma-rosa: sem fragrâncias artificiais, mas não indicado para quem reage a óleos essenciais.',
    actives: ['Óleo de girassol ozonizado', 'Óleo de rosa-mosqueta', 'Lavanda', 'Palma-rosa'], photo: balImg.url,
  },
];

export const ColorSelector = () => {
  const [active, setActive] = useState<Tone>('maos');
  const { map } = useProductsByHandle();
  const addToCart = useAddToCart();
  useViewItemList('Seletor de cuidados', OPTIONS.map((o) => map.get(o.handle)));

  const opt = OPTIONS.find((o) => o.key === active)!;
  const tone = TONE[opt.key];
  const product = map.get(opt.handle);
  const packshot = product?.node.images.edges[0]?.node;
  const chooseTone = (option: typeof OPTIONS[number]) => {
    setActive(option.key);
    try { trackGenericEvent('home_selector_color', { produto: option.handle }); } catch {}
  };

  return (
    <section id="escolha" className={cn('scroll-mt-20 text-v2-ink py-16 md:py-24 transition-colors duration-500', tone.bg)}>
      <Reveal className="container mx-auto px-5 max-w-6xl">
        <Eyebrow>Escolha pelo que sua pele precisa</Eyebrow>
        <H2 className="max-w-2xl">Mãos, pés ou corpo. Uma cor para cada cuidado.</H2>

        <div role="tablist" aria-label="Escolha o cuidado" className="mt-8 flex flex-wrap gap-2">
          {OPTIONS.map((o) => (
            <button
              key={o.key}
              role="tab"
              aria-selected={o.key === active}
              onClick={() => chooseTone(o)}
              className={cn(
                'inline-flex min-h-12 items-center gap-2 rounded-full border px-4 text-sm transition-colors',
                o.key === active ? 'bg-background border-v2-ink/30 font-semibold shadow-sm' : 'border-v2-ink/15 hover:bg-background/60',
              )}
            >
              <span className={cn('w-3 h-3 rounded-full', TONE[o.key].dot)} aria-hidden />
              {o.label}
            </button>
          ))}
        </div>
        <Link
          to="/quiz"
          onClick={() => { try { trackGenericEvent('click_quiz_home'); } catch {} }}
          className="mt-4 inline-flex min-h-12 items-center text-sm font-semibold text-v2-muted underline underline-offset-4 hover:text-v2-ink"
        >
          Não sabe qual escolher? Responda 3 perguntas
        </Link>

        <div className="mt-10 grid md:grid-cols-2 gap-8 md:gap-12 items-start" role="tabpanel" aria-live="polite">
          <div className="rounded-3xl bg-background aspect-square flex items-center justify-center overflow-hidden">
            {packshot ? (
              <img
                key={packshot.url}
                src={shopifyImg(packshot.url, 800)}
                alt={packshot.altText || product!.node.title}
                width={800}
                height={800}
                loading="lazy"
                className="w-full h-full object-contain animate-fade-in"
              />
            ) : (
              <div className="w-1/2 h-2/3 rounded-xl bg-v2-ink/5 animate-pulse" />
            )}
          </div>

          <div className="min-w-0">
            <h3 className="font-narrow text-3xl md:text-4xl font-semibold leading-tight">
              {product?.node.title ?? <span className="inline-block h-9 w-64 bg-v2-ink/5 rounded animate-pulse" />}
            </h3>
            <p className={cn('mt-2 font-semibold', tone.text)}>{opt.phrase}</p>
            <p className="mt-3 text-v2-muted">{opt.text}</p>
            {opt.key === 'bal' && (
              <div className="mt-4 flex flex-wrap gap-2 text-sm">
                <span className="rounded-full border border-v2-bal/30 bg-background px-3 py-1.5">Com óleos essenciais de lavanda e palma-rosa</span>
                {!isSoldOut(product?.node) && (
                  <span className="rounded-full bg-v2-bal/10 px-3 py-1.5 font-semibold text-v2-bal">Últimas unidades deste lote</span>
                )}
              </div>
            )}
            <ul className="mt-4 flex flex-wrap gap-2">
              {opt.actives.map((a) => (
                <li key={a} className="text-sm px-3 py-1.5 rounded-full bg-background border border-v2-ink/10">{a}</li>
              ))}
            </ul>
            <PriceBlock product={product} className="mt-5" />
            <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
              {product && isSoldOut(product.node) ? (
              <Link to={`/produto/${opt.handle}`} className="inline-flex items-center justify-center gap-2 min-h-12 px-6 rounded-full border border-v2-ink/20 font-semibold">
                Esgotado · Avise-me
              </Link>
              ) : (
              <button
                onClick={() => addToCart(product)}
                disabled={!product}
                className={cn('inline-flex items-center justify-center gap-2 min-h-12 px-6 rounded-full text-background font-semibold disabled:opacity-50 transition-colors', tone.btn)}
              >
                <ShoppingBag className="w-4 h-4" /> Adicionar ao carrinho
              </button>
              )}
              <Link to={`/produto/${opt.handle}`} className="inline-flex items-center justify-center gap-1 min-h-12 px-3 font-medium underline underline-offset-4">
                Ver detalhes <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <PurchaseBenefits />
            <div className="mt-6 rounded-2xl overflow-hidden aspect-[3/2]">
              <img key={opt.photo} src={opt.photo} alt={opt.phrase} width={900} height={600} loading="lazy" decoding="async" className="w-full h-full object-cover animate-fade-in" />
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
};
